import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardHeader,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Paper,
  IconButton,
} from '@mui/material';
import {
  AccountBalance as FinanceIcon,
  People as HRIcon,
  Handyman as EstatesIcon,
  ArrowBack as BackIcon,
  CheckCircle as DoneIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

export const BusinessAnalytics: React.FC = () => {
  const navigate = useNavigate();

  const businessFunctions = [
    {
      title: 'Finance & ROI',
      icon: <FinanceIcon color="primary" />,
      description: 'Cross-school budget analysis and cost-per-outcome modeling.',
      status: 'Ready for Integration',
      items: [
        'Automatic spend correlation with skill outcomes',
        'Budget vs. Actuals trust-wide overview',
        'ROI calculator for intervention programs',
      ]
    },
    {
      title: 'HR & Expertise',
      icon: <HRIcon color="secondary" />,
      description: 'Trust-wide staff CPD tracking and skill deployment.',
      status: 'Conceptual Outline',
      items: [
        'Staff expertise inventory (Skills Mapping for Adults)',
        'Deployment optimization based on student disengagement signals',
        'CPD impact analysis on classroom data',
      ]
    },
    {
      title: 'Estates & Facilities',
      icon: <EstatesIcon sx={{ color: '#4caf50' }} />,
      description: 'Facility condition and maintenance request automation.',
      status: 'Frontend Prototype',
      items: [
        'Integrated maintenance request forms (linked to Data Lake)',
        'Strategic building condition overview',
        'Correlation between room environment and behavioral flags',
      ]
    }
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center' }}>
        <IconButton onClick={() => navigate('/dashboard')} sx={{ mr: 2 }}>
          <BackIcon />
        </IconButton>
        <Typography variant="h4" component="h1" fontWeight="bold">
          Non-School Functions: Strategic Insights
        </Typography>
      </Box>

      <Paper elevation={0} sx={{ p: 3, mb: 4, bgcolor: '#e3f2fd', borderRadius: 2 }}>
        <Typography variant="body1" color="primary.dark">
          <strong>Meeting Note:</strong> This section demonstrates how the "Integrated Insight Markbook" 
          architecture (built on Weaviate) extends beyond the classroom to serve Trust-wide operations 
          and the Business Manager functions.
        </Typography>
      </Paper>

      <Grid container spacing={3}>
        {businessFunctions.map((func) => (
          <Grid item xs={12} md={4} key={func.title}>
            <Card sx={{ height: '100%', borderRadius: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
              <CardHeader 
                avatar={func.icon}
                title={func.title}
                titleTypographyProps={{ variant: 'h6', fontWeight: 'bold' }}
                subheader={func.status}
              />
              <CardContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {func.description}
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <List dense>
                  {func.items.map((item) => (
                    <ListItem key={item} disableGutters>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <DoneIcon fontSize="small" color="disabled" />
                      </ListItemIcon>
                      <ListItemText primary={item} primaryTypographyProps={{ variant: 'body2' }} />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Box sx={{ mt: 6, p: 3, border: '1px dashed #bdbdbd', borderRadius: 2 }}>
        <Typography variant="h6" gutterBottom>
          The Unified Data Lake Vision
        </Typography>
        <Typography variant="body2" color="text.secondary">
          By utilizing the same API structure as the Skills Map, these operational modules feed into a 
          "Strategic Dashboard" that connects <strong>Physical Facilities</strong>, <strong>Human Resources</strong>, 
          and <strong>Financial Health</strong> directly to <strong>Student Outcomes</strong>. This eliminates 
          the fragmentation of 13 separate school spreadsheets.
        </Typography>
      </Box>
    </Container>
  );
};

// Internal Import helper
